import React from 'react';

import { StagedValue } from '@/ux-common/components/staged-value/staged-value';

import type { PreviewChangeData } from './setting-preview.types';

/**
 * PreviewChange
 *
 * Pure presentational atom that renders a "new value ← old value" indicator.
 * Used by `DirtyChangeTooltip`, and anywhere a compact change display is needed.
 *
 * NOT used by `PreviewItem`, which hand-rolls the same lockup. That row needs two
 * things the registry `StagedValue` does not offer: a `grid-cols-[1fr_auto_1fr]`
 * track so the arrow lands at the same x-position on every row of a list, and
 * `truncate` on each value. `.staged-value` is `inline-flex` and `nowrap`, so it
 * sizes to content and cannot do either. Collapsing the two needs a block/grid
 * variant in the registry first — do not "fix" the duplication by swapping this
 * component in.
 *
 * Both values must be pre-formatted strings — this component does no formatting.
 *
 * Thin wrapper over the registry `StagedValue`, which owns the lockup: the two
 * value slots, the arrow, and the new/old coloring (`--ckds-text-fg-dirty` /
 * `--ckds-text-fg-old`). Kept as a named component because callers pass a
 * `PreviewChangeData` shape rather than the registry's prop names.
 */
export const PreviewChange = ({
  displayCurrent,
  displayOriginal,
}: PreviewChangeData): React.ReactElement => {
  return (
    <StagedValue
      data-component="PreviewChange"
      newValue={displayCurrent}
      oldValue={displayOriginal}
    />
  );
};
